/**
 * Native platform utilities for Capacitor integration.
 * These functions handle platform-specific features for iOS, Android, and Web.
 */

import { Capacitor, SystemBars, SystemBarsStyle } from '@capacitor/core';

// Check if we're running in a native app context
export const isNative = () => {
    return Capacitor.isNativePlatform();
};

// Get the current platform
export const getPlatform = () => {
    return Capacitor.getPlatform(); // 'ios', 'android', or 'web'
};

// Check specific platforms
export const isIOS = () => getPlatform() === 'ios';
export const isAndroid = () => getPlatform() === 'android';
export const isWeb = () => getPlatform() === 'web';

// Initialize native features
export const initializeNative = async () => {
    if (!isNative()) {
        console.log('Running in web mode');
        return;
    }

    // Add platform class to body for CSS targeting
    document.body.classList.add(`platform-${getPlatform()}`);


    try {
        // SystemBars (bundled in @capacitor/core 8) is the edge-to-edge-aware
        // replacement for the StatusBar plugin's setStyle. It styles the
        // navigation bar as well, which matters now that targetSdk 36 forces
        // edge-to-edge and the WebView paints behind both bars.
        //
        // We deliberately do NOT set a status bar background colour. Capacitor's
        // `overlaysWebView: true` forces the status bar transparent on every API
        // level (StatusBar.setOverlaysWebView -> setStatusBarColor(TRANSPARENT)),
        // so setBackgroundColor was always a no-op here — and on API 35+ it is a
        // deprecated call that Play flags under "deprecated APIs for edge-to-edge".
        const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
        await SystemBars.setStyle({
            style: isDarkMode ? SystemBarsStyle.Dark : SystemBarsStyle.Light
        });

        console.log('Native features initialized');
    } catch (error) {
        console.log('SystemBars not available:', error);
    }

    try {
        // Initialize RevenueCat for in-app purchases.
        //
        // RevenueCat issues a SEPARATE public SDK key per store: `appl_...` for
        // App Store, `goog_...` for Play. A single key cannot serve both. This
        // used to read one REACT_APP_REVENUECAT_API_KEY for every platform, and
        // production had the Apple key in it — so every Android launch failed
        // with "The specified API Key is not recognized" and no Android user
        // could buy a streak freeze.
        const platform = getPlatform();
        const rcApiKey = platform === 'ios'
            // Fall back to the legacy single-key var so iOS keeps working if
            // only the old name is set.
            ? (process.env.REACT_APP_REVENUECAT_API_KEY_IOS || process.env.REACT_APP_REVENUECAT_API_KEY)
            : platform === 'android'
                ? process.env.REACT_APP_REVENUECAT_API_KEY_ANDROID
                : null;

        const expectedPrefix = platform === 'ios' ? 'appl_' : 'goog_';
        if (rcApiKey && !rcApiKey.startsWith(expectedPrefix)) {
            // Loud, because the failure is otherwise silent: purchases simply
            // never work on that platform.
            console.error(
                `[RevenueCat] API key for ${platform} should start with "${expectedPrefix}". ` +
                `Purchases will fail. Set REACT_APP_REVENUECAT_API_KEY_${platform.toUpperCase()}.`
            );
        }

        if (rcApiKey && window.Capacitor?.Plugins?.Purchases) {
            const { Purchases } = window.Capacitor.Plugins;
            await Purchases.configure({ apiKey: rcApiKey });
            console.log(`RevenueCat initialized (${platform})`);
        } else if (!rcApiKey) {
            console.warn(`[RevenueCat] No API key configured for ${platform}; purchases disabled.`);
        }
    } catch (error) {
        console.log('RevenueCat not available:', error);
    }
    
    try {
        // Keyboard handling — OS-managed layout.
        //
        // Capacitor's resize:'body' sets body.style.height natively
        // on both platforms. We do NOT touch body.style.height in JS.
        //
        // Our listeners only:
        //   1. Toggle body.keyboard-open for CSS padding clears
        //   2. Set --keyboard-height for modal positioning
        //   3. Hide/resume AdMob banner on Android (ADJUST_RESIZE
        //      makes the banner ride up — hide it, fire-and-forget)
        const { Keyboard } = await import('@capacitor/keyboard');

        Keyboard.addListener('keyboardWillShow', (info) => {
            document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
            document.body.classList.add('keyboard-open');
            // On Android with ADJUST_RESIZE, the AdMob banner rides up
            // with the shrinking layout. Hide it — the keyboard covers
            // that area anyway. iOS doesn't need this (banner stays put).
            if (isAndroid() && window.Capacitor?.Plugins?.AdMob) {
                window.Capacitor.Plugins.AdMob.hideBanner().catch(() => {});
            }
        });

        Keyboard.addListener('keyboardWillHide', () => {
            document.body.style.setProperty('--keyboard-height', '0px');
            document.body.classList.remove('keyboard-open');
            if (isAndroid() && window.Capacitor?.Plugins?.AdMob) {
                window.Capacitor.Plugins.AdMob.resumeBanner().catch(() => {});
            }
        });
    } catch (error) {
        console.log('Keyboard plugin not available:', error);
    }
};

// Update status bar when theme changes
export const updateStatusBarTheme = async (isDarkMode) => {
    if (!isNative()) return;
    
    try {
        await SystemBars.setStyle({
            style: isDarkMode ? SystemBarsStyle.Dark : SystemBarsStyle.Light
        });
    } catch (error) {
        console.log('Could not update system bars:', error);
    }
};

// Handle back button for Android
export const setupBackButton = async (onBackPress) => {
    if (!isNative()) return;
    
    try {
        const { App } = await import('@capacitor/app');
        
        App.addListener('backButton', ({ canGoBack }) => {
            if (canGoBack) {
                window.history.back();
            } else if (onBackPress) {
                onBackPress();
            } else {
                App.exitApp();
            }
        });
    } catch (error) {
        console.log('App plugin not available:', error);
    }
};

// Get the API base URL based on platform
export const getApiBaseUrl = () => {
    if (isNative()) {
        // For native apps, you need to use the full URL to your backend
        // This should be configured based on your deployment
        return process.env.REACT_APP_API_URL || 'https://word-labyrinth.com';
    }
    // For web, use relative URLs (proxied in development)
    return '';
};

const nativeUtils = {
    isNative,
    getPlatform,
    isIOS,
    isAndroid,
    isWeb,
    initializeNative,
    updateStatusBarTheme,
    setupBackButton,
    getApiBaseUrl,
};

export default nativeUtils;
